/**
 * What every workflow status on the Console means, in the operator's words:
 * what the status is, why the item is in it, and what (if anything) someone
 * has to do next. StatusTag.jsx shows it as the tooltip of a status tag, so
 * a person who meets "Awaiting Clearance" or "Quarantined" never has to ask
 * what it means.
 *
 * Kept free of React, so node --test reads it directly (statusHelp.test.js),
 * and kept in one place, so the wording stays consistent from screen to
 * screen.
 *
 * Wording rules (the owner's brief, 8 Oct 2026):
 *   - the server's word is "order"; the person's word is "transaction". Codes
 *     (states, claims, API paths) never change - only what a person reads;
 *   - every entry says what it is AND what happens next - "nothing to do"
 *     is an answer too, and saves someone a question;
 *   - one or two short sentences: it is a tooltip, not a manual.
 *
 * Each kind is a map from the code the server sends to { label, help }.
 * `label` is given where the screen has no friendly name of its own today
 * (an upload batch showed ACCEPTANCE_EXPIRED as it is); where the screen
 * already has one (orders.js STATE_LABEL), the screen keeps it and only the
 * help comes from here.
 */

/** The kinds of status this module explains: one per workflow. */
export const STATUS_KIND = Object.freeze({
  TRANSACTION: 'transaction',
  HOLD_REASON: 'holdReason',
  UPLOAD_BATCH: 'uploadBatch',
  HOLDING_CHECK: 'holdingCheck',
  NAV_RUN: 'navRun',
  PRICE_CHECK: 'priceCheck',
  FEE_PROPOSAL: 'feeProposal',
  BUSINESS_DAY: 'businessDay',
  BOOK: 'book',
  STATEMENT_MATCH: 'statementMatch',
  STATEMENT_CLEARANCE: 'statementClearance',
  STATEMENT_AGE: 'statementAge',
  USER: 'user',
});

// A small helper so every entry below reads as one line of label + help.
const entry = (label, help) => Object.freeze({ label, help });

/**
 * A transaction's state (order.state on the server; orders.js STATE_LABEL
 * keeps the label). The journey, in short: LIV reports it, intake checks it,
 * the money arrives (subscriptions), a maker reviews it before the cut-off,
 * the book closes, the price is published, a checker approves it, and the
 * register is updated (docs/orders/order-lifecycle-and-unit-checks.md).
 */
const TRANSACTION = Object.freeze({
  RECEIVED: entry('Received',
    'Just arrived from LIV and being checked. It moves on by itself in a moment: nothing to do.'),
  AWAITING_LIV: entry('Waiting for LIV',
    'Submitted in LIV, but LIV is still collecting signatures. It has no dealing date yet, so it cannot be reviewed; a redemption\'s units are already reserved. '
    + 'Nothing to do: it moves on when LIV completes it, or is cancelled if LIV aborts it.'),
  VALIDATED: entry('Validated',
    'Passed the intake checks and has a dealing date. Next: review it, with a comment, before its dealing date\'s cut-off, or it rolls to the next dealing day.'),
  AWAITING_FUNDS: entry('Awaiting Funds',
    'A subscription waiting for the investor\'s money to appear on a bank statement. You can review it now. '
    + 'If the money is on the statement, match it on Bank Statement Transactions; with no cleared money in five business days it expires.'),
  AWAITING_CLEARANCE: entry('Awaiting Clearance',
    'Paid by cheque: the money is matched, but the bank has not cleared it yet. '
    + 'Next: record the clearance date on Bank Statement Transactions once the bank makes the money available.'),
  MATCHED_CLEARED: entry('Funds Cleared',
    'The money is matched and cleared. Next: make sure it is reviewed before the cut-off; it is frozen into its dealing day when the book closes.'),
  FROZEN: entry('Frozen (book closed)',
    'Its dealing day\'s book has closed and it is locked into that day: it can no longer be cancelled or changed. '
    + 'Next: it waits for the fund\'s unit price for that date to be approved on Fund Register.'),
  PRICED: entry('Priced',
    'The unit price for its dealing date is published and its units are worked out. It moves to Ready for Approval by itself.'),
  READY: entry('Ready for Approval',
    'Reviewed and priced. Next: a checker who did not review it approves it, sends it back for a new review, or rejects it.'),
  APPROVED: entry('Approved',
    'Approved by a checker. Its units are being written to the register: nothing to do.'),
  ALLOCATED: entry('Allocated',
    'Its units are in the register. LIV is being told, and it becomes Posted once that is sent: nothing to do.'),
  POSTED: entry('Posted',
    'Complete: the register is updated and LIV has been told.'),
  REJECTED: entry('Rejected',
    'Rejected by the intake checks or by a checker; its history says why. It will not be dealt, and any reserved units are free again. '
    + 'Money already matched to it is flagged for refund.'),
  EXPIRED: entry('Expired',
    'A subscription whose money did not arrive, or did not clear, in time. It will not be dealt; the investor has to submit it again.'),
  CANCELLED: entry('Cancelled',
    'Cancelled before it was dealt, usually because LIV withdrew it (for example, a signatory refused). Any reserved units are free again.'),
  // IP-200 (owner, 19 Oct 2026): a held transaction is shown with its reason, and rejecting is the
  // only thing Lynear does with it in the Console. LIV was told it is held when intake held it.
  HELD: entry('On Hold',
    'Stopped at intake; the reason is shown under the status, and LIV has been told it is on hold. '
    + 'It cannot be reviewed or approved. If it should not go ahead, reject it with a reason; LIV tells the investor.'),
});

/**
 * Why a transaction is HELD (orders/intake.js HOLD_REASON; orders.js HOLD_REASON_LABEL keeps the label).
 * IP-200: each says why, and that rejecting is what can be done - there is no release in the Console.
 */
const HOLD_REASON = Object.freeze({
  'HLD-002': entry('Fund suspended',
    'The fund was suspended when this arrived. Reject it if it should not go ahead.'),
  'HLD-003': entry('Redemption restricted',
    'The fund had a redemption restriction in force when this arrived. Reject it if it should not go ahead.'),
  'HLD-004': entry('Holder dormant',
    'The investor\'s holder record was dormant when this arrived. Check the holder; reject it if it should not go ahead.'),
  LATE_DELIVERY: entry('Late delivery from LIV',
    'LIV delivered it more than 7 days after the investor completed it. Confirm with LIV whether it is still wanted; reject it if not.'),
  NO_PRICE_FOR_ESTIMATE: entry('No price for the estimate',
    'A redemption asked for in rupees, but the fund had no unit price to estimate how many units to reserve. Reject it if it should not go ahead.'),
  PAYOUT_ACCOUNT_UNREGISTERED: entry('Payout account not registered',
    'The redemption would pay a bank account the investor never registered. Check the account with the investor; reject it if it is wrong.'),
});

/** An uploaded file's batch state (intake/batch.js; UploadsTab.jsx shows the tag). */
const UPLOAD_BATCH = Object.freeze({
  RECEIVED: entry('Received',
    'The file is stored and waiting for its malware scan to start: nothing to do.'),
  SCANNING: entry('Scanning',
    'Being scanned for malware, usually for a minute or two. Wait for the result.'),
  VALIDATED: entry('Ready for a decision',
    'Scanned and checked. Next: another person accepts it or rejects it, before it expires.'),
  QUARANTINED: entry('Quarantined',
    'Some rows have errors, so the file cannot be accepted. Correct the file and upload it again.'),
  REJECTED_MALWARE: entry('Malware found',
    'The scan found a threat. Nothing in the file is used, and an administrator has been alerted.'),
  SCAN_FAILED: entry('Not checked',
    'The scan could not check the file, so nothing in it is used. Upload it again; tell an administrator if it happens twice.'),
  ACCEPTED: entry('Accepted',
    'Accepted, and the system is now loading it (for example, matching statement lines or building NAV runs): nothing to do.'),
  PROCESSED: entry('Processed',
    'Accepted and fully loaded. For a price file, approve the NAV runs it made on Fund Register.'),
  REJECTED: entry('Rejected',
    'Rejected by a checker or by the system; the reason is in its details. Nothing in it is used. Upload a corrected file if one is needed.'),
  SUPERSEDED: entry('Superseded',
    'A later upload of the same file replaced this one before it was decided. Use the newer upload.'),
  ACCEPTANCE_EXPIRED: entry('Expired',
    'Nobody accepted it in time (a daily file by midnight on its own day; others within 48 hours). Upload it again to use it.'),
});

/** A holding check (intake/holding-checks.js): one register file row against the register. */
const HOLDING_CHECK = Object.freeze({
  MATCHED: entry('Matched', 'The file\'s units agree with the register: nothing to do.'),
  BREAK: entry('Break', 'The file and the register disagree on the units. Investigate the difference before relying on either figure.'),
  UNKNOWN_CLIENT: entry('Unknown client', 'The file names an account the register cannot map to a holder, so it was recorded but not compared. Map the client, then check again.'),
  NOT_IN_FILE: entry('Not in file', 'The register holds units for this client, but the file does not list them. Check whether the file is complete.'),
});

/** A NAV run's state (unitPrices/runs.js RUN_STATES). */
const NAV_RUN = Object.freeze({
  CHECKED: entry('CHECKED',
    'Its checks passed. Next: someone other than the uploader approves it, which publishes the unit price, or rejects it.'),
  QUARANTINED: entry('QUARANTINED',
    'A check failed, so it cannot be approved yet. Next: Compliance may override a day-on-day move with a reason; otherwise reject it and upload a corrected file.'),
  APPROVED: entry('APPROVED (OLD PROCESS)',
    'Approved before the Trustee step was removed, so it can no longer be published. Reject it and upload the file again.'),
  PUBLISHED: entry('PUBLISHED',
    'Approved: its unit price is published and used to price transactions. Nothing to do.'),
  REJECTED: entry('REJECTED',
    'Rejected; its price is not used. Upload a corrected file if the fund still needs a price for that date.'),
  SUPERSEDED: entry('SUPERSEDED',
    'A later version of the price for the same fund and date replaced this one. Nothing to do.'),
});

/** A NAV run check's outcome (unitPrices/runs.js OUTCOME_COLOUR). */
const PRICE_CHECK = Object.freeze({
  PASS: entry('PASS', 'The check passed: nothing to do.'),
  WARN: entry('WARN', 'The check passed with a warning. Read it before you approve the run.'),
  FAIL: entry('FAIL', 'The check failed, so the run is quarantined until it is overridden (day-on-day move only) or rejected.'),
  NOT_APPLICABLE: entry('NOT APPLICABLE', 'The check does not apply to this fund or date.'),
  OVERRIDDEN: entry('OVERRIDDEN', 'The check failed, but Compliance overrode it with a reason. The run can now be approved.'),
});

/** A fund fee change proposal (fees/sql.js; FundConfig.jsx). */
const FEE_PROPOSAL = Object.freeze({
  PROPOSED: entry('Proposed',
    'A change to the fund\'s fees, waiting for a second person to approve or reject it before its effective date.'),
  APPROVED: entry('Approved',
    'Approved: the new fees apply from the effective date.'),
  REJECTED: entry('Rejected',
    'Rejected: the fund\'s fees did not change.'),
});

/** A business day's stage (business-day/stages.js STAGES keeps the label). */
const BUSINESS_DAY = Object.freeze({
  OPEN: entry('Open for transactions',
    'The day is taking transactions. Review each one before the cut-off; any not reviewed by then roll to the next dealing day.'),
  FROZEN: entry('Cut-off · frozen',
    'The cut-off has passed and the day\'s books are closed. Next: the day waits for each fund\'s unit price to be approved on Fund Register.'),
  PRICED: entry('Priced',
    'Every fund that needs a price has one. The Transaction pricing step starts by itself: nothing to do.'),
  REPRICING: entry('Transaction pricing',
    'Open transactions are being priced at the new prices. Reviews are paused for up to 15 minutes; wait for it to finish.'),
  RECONCILING: entry('Reconciliation & approvals',
    'Load and match the day\'s bank statements, and approve or reject the day\'s transactions. '
    + 'The day ends once every statement is loaded, the funding window has closed and none of its transactions is pending.'),
  DAY_END: entry('Day end',
    'The day\'s summary is being written. Reviews are paused for up to 5 minutes: nothing to do.'),
  CLOSED: entry('Closed',
    'The day is finished and its summary is written. Nothing to do.'),
});

/** A fund's dealing book for one day (order_book.state; the business day drawer shows it per fund). */
const BOOK = Object.freeze({
  OPEN: entry('Open',
    'Taking transactions for this dealing day until the fund\'s cut-off. Review them before then.'),
  CLOSED: entry('Closed',
    'Closed at the cut-off: no more transactions can join, and the reviewed ones are frozen into the day. Next: the fund\'s unit price.'),
});

/** A bank statement line's match state (bankStatement/statementLines.js MATCH_TAG keeps the label). */
const STATEMENT_MATCH = Object.freeze({
  AUTO: entry('Auto reconciled',
    'Matched to its transaction automatically, by its reference and amount. Nothing to do.'),
  MANUAL_APPROVED: entry('Manually reconciled',
    'Matched by one person and approved by a second. Nothing to do.'),
  MANUAL_PENDING: entry('Awaiting approval',
    'Someone matched this line to a transaction. Next: a second person approves or rejects the match.'),
  UNRECONCILED: entry('Unreconciled',
    'Not matched to any transaction yet. Match it - a credit to its subscription, a debit to the redemption it paid. A credit that belongs to no'
      + ' transaction is refunded once the bank has cleared it; any other left-over line is cleared by hand.'),
  REFUNDED: entry('Refunded',
    'The money was sent back to the account it came from. Nothing to do.'),
});

/** A cheque's clearance (statementLines.js CLEARANCE_TAG keeps the label). */
const STATEMENT_CLEARANCE = Object.freeze({
  AWAITING_CLEARANCE: entry('Awaiting clearance',
    'A cheque the bank has not cleared yet. Next: record the date the bank made the money available, or record it as returned.'),
  CLEARED: entry('Cleared', 'The bank has made the money available. Nothing to do.'),
  RETURNED: entry('Returned', 'The bank returned the cheque, so the money never arrived. Nothing more to do on this line.'),
});

/** An unmatched credit's age ladder (cash/receipts.js, IP-71: business days since its value date). */
const STATEMENT_AGE = Object.freeze({
  NEW: entry('New', 'Unmatched for up to 1 business day. Match it to its transaction.'),
  INVESTIGATE: entry('Investigate', 'Unmatched for 2 to 4 business days. Find out who sent it and what it is for.'),
  ESCALATE: entry('Escalate', 'Unmatched for 5 to 9 business days. Escalate it to your team lead.'),
  RETURN_DUE: entry('Return due', 'Unmatched for 10 business days or more. Refund it to the account it came from.'),
});

/** A Console user's status (UsersPanel.jsx). */
const USER = Object.freeze({
  INVITED: entry('Invited', 'Invited, but has not finished signing in for the first time yet. If they cannot find the invitation, resend it.'),
  ACTIVE: entry('Active', 'Can sign in and use the Console with the roles shown.'),
  SUSPENDED: entry('Suspended', 'Cannot sign in until an administrator reinstates them.'),
});

/** Every kind's map, by STATUS_KIND. */
export const STATUS_HELP = Object.freeze({
  [STATUS_KIND.TRANSACTION]: TRANSACTION,
  [STATUS_KIND.HOLD_REASON]: HOLD_REASON,
  [STATUS_KIND.UPLOAD_BATCH]: UPLOAD_BATCH,
  [STATUS_KIND.HOLDING_CHECK]: HOLDING_CHECK,
  [STATUS_KIND.NAV_RUN]: NAV_RUN,
  [STATUS_KIND.PRICE_CHECK]: PRICE_CHECK,
  [STATUS_KIND.FEE_PROPOSAL]: FEE_PROPOSAL,
  [STATUS_KIND.BUSINESS_DAY]: BUSINESS_DAY,
  [STATUS_KIND.BOOK]: BOOK,
  [STATUS_KIND.STATEMENT_MATCH]: STATEMENT_MATCH,
  [STATUS_KIND.STATEMENT_CLEARANCE]: STATEMENT_CLEARANCE,
  [STATUS_KIND.STATEMENT_AGE]: STATEMENT_AGE,
  [STATUS_KIND.USER]: USER,
});

/** A code the module has no words for, made readable: ACCEPTANCE_EXPIRED -> "Acceptance expired". */
export function humanise(code) {
  const text = String(code ?? '').replace(/_/g, ' ').toLowerCase().trim();
  return text ? text[0].toUpperCase() + text.slice(1) : '';
}

/** The tooltip for a status, or null when there is none (an unknown kind or a code the server added later). */
export function statusHelp(kind, code) {
  return STATUS_HELP[kind]?.[code]?.help ?? null;
}

/** A status's name as the person reads it: this module's label, else the code made readable - never a raw code. */
export function statusLabel(kind, code) {
  return STATUS_HELP[kind]?.[code]?.label ?? humanise(code);
}
